import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminRowMenuItem,
  AdminPageTitle,
  AdminRowActionsButton,
  AdminRowAvatar,
  AdminSearchField,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminListFilterDropdown,
  AdminRowMenuIcon,
  ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import ProfilePlaceholder from '../../../assets/img/User.png';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import { applyComplianceQueueSession } from '../../../utils/complianceCaseSession';

/** Queue Case Status values (filter + row badges). Decided cases remain until Archive handoff succeeds. */
export const COMPLIANCE_QUEUE_STATUSES = [
  'New',
  'Under Review',
  'Awaiting Information',
  'Decided',
];

const COMPLIANCE_TYPES = ['Licence', 'Badge', 'Ticket'];
const CASE_TYPES = ['New', 'Renewal', 'Replacement'];

const DEFAULT_FILTERS = {
  type: 'All',
  category: 'All',
  status: 'All',
};

const TEAM_MEMBER_NAMES = ['Jordan Lee', 'Mia Chen', 'Olivia Smith', 'Daniel Price'];

const TRADE_CATEGORIES = [
  'Plumbing',
  'Electrical',
  'Building',
  'Gas Fitting',
  'Carpentry',
  'Roofing',
  'Painting',
  'Landscaping',
];

const CATEGORY_FILTER_OPTIONS = ['All Trades', ...TRADE_CATEGORIES];

const COMPLIANCE_ITEMS_BY_TYPE = {
  Licence: [
    { id: 'LIC-0001', name: 'Electrical Licence', tradeCategory: 'Electrical' },
    { id: 'LIC-0002', name: 'Plumbing Licence', tradeCategory: 'Plumbing' },
    { id: 'LIC-0003', name: 'Gas Fitting Licence', tradeCategory: 'Plumbing' },
  ],
  Badge: [
    { id: 'BDG-0001', name: 'First Aid', tradeCategory: 'All Trades' },
    { id: 'BDG-0002', name: 'Industry Membership', tradeCategory: 'All Trades' },
    { id: 'BDG-0003', name: 'Working with Children Check', tradeCategory: 'All Trades' },
  ],
  Ticket: [
    { id: 'TKT-0001', name: 'White Card', tradeCategory: 'All Trades' },
    { id: 'TKT-0002', name: 'Working at Heights', tradeCategory: 'All Trades' },
    { id: 'TKT-0003', name: 'Confined Spaces', tradeCategory: 'All Trades' },
  ],
};

const parseSampleDate = (day, monthShort, year, hour, minute, isPm) => {
  const months = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
  let h = hour;
  if (isPm && h < 12) h += 12;
  if (!isPm && h === 12) h = 0;
  return new Date(year, months[monthShort], day, h, minute, 0, 0).toISOString();
};

/** Spec sample rows. Source IDs follow LBT + Configuration — Compliance. */
export const QUEUE_SPEC_SAMPLES = [
  {
    compliance_id: 'CMP-004281',
    business_name: 'Apex Plumbing',
    business_id: 'BUS-001284',
    abn: '12 345 678 901',
    account_holder_legal_name: 'Mark Taylor',
    account_status: 'Active',
    compliance_type: 'Licence',
    compliance_item_id: 'LIC-0002',
    compliance_item: 'Plumbing Licence',
    trade_category: 'Plumbing',
    business_trade_category: 'Plumbing',
    case_type: 'Renewal',
    support_team_member: 'Jordan Lee',
    case_status: 'Under Review',
    submitted_at: parseSampleDate(3, 'Apr', 2026, 9, 0, false),
    last_updated: parseSampleDate(3, 'Apr', 2026, 10, 5, false),
  },
  {
    compliance_id: 'CMP-004282',
    business_name: 'Bright Spark Electrical',
    business_id: 'BUS-001285',
    account_status: 'Active',
    compliance_type: 'Ticket',
    compliance_item_id: 'TKT-0001',
    compliance_item: 'White Card',
    trade_category: 'All Trades',
    business_trade_category: 'Electrical',
    case_type: 'New',
    support_team_member: 'Mia Chen',
    case_status: 'Awaiting Information',
    submitted_at: parseSampleDate(3, 'Apr', 2026, 9, 18, false),
    last_updated: parseSampleDate(3, 'Apr', 2026, 10, 42, false),
  },
  {
    compliance_id: 'CMP-004283',
    business_name: 'Urban Gas Works',
    business_id: 'BUS-001286',
    account_status: 'Active',
    compliance_type: 'Licence',
    compliance_item_id: 'LIC-0003',
    compliance_item: 'Gas Fitting Licence',
    trade_category: 'Plumbing',
    business_trade_category: 'Plumbing',
    case_type: 'New',
    support_team_member: null,
    case_status: 'New',
    submitted_at: parseSampleDate(2, 'Apr', 2026, 16, 10, true),
    last_updated: parseSampleDate(3, 'Apr', 2026, 8, 35, false),
  },
  {
    compliance_id: 'CMP-004284',
    business_name: 'Metro Building Group',
    business_id: 'BUS-001287',
    account_status: 'Active',
    compliance_type: 'Licence',
    compliance_item_id: 'LIC-0001',
    compliance_item: 'Electrical Licence',
    trade_category: 'Electrical',
    business_trade_category: 'Building',
    case_type: 'Replacement',
    support_team_member: 'Jordan Lee',
    case_status: 'Decided',
    submitted_at: parseSampleDate(2, 'Apr', 2026, 14, 35, true),
    last_updated: parseSampleDate(3, 'Apr', 2026, 11, 20, false),
  },
];

export const findQueueSpecSample = (complianceId) =>
  QUEUE_SPEC_SAMPLES.find((row) => row.compliance_id === complianceId) || null;

/** Spec sample rows + generated open queue cases */
const buildMockDocuments = () => {
  const samples = QUEUE_SPEC_SAMPLES;

  const businessNames = [
    'Northside Plumbing Co.', 'Bolt Electrical Services', 'Coastal Gas & Heating',
    'Modern Painters Collective', 'Master Builders Guild', 'Green Lawn Care Pty Ltd',
    'Smart Locksmith Co.', 'Elite Flooring Professionals', 'QuickFix Tiling & Reno',
    'Rapid Roofing Group', 'Ace Carpentry Solutions', 'Top Tilers & Bathrooms', 'Clean Windows Pro',
  ];

  const extra = [];
  const now = new Date();
  for (let i = 0; i < 40; i++) {
    const n = 4286 + i;
    const complianceType = COMPLIANCE_TYPES[i % COMPLIANCE_TYPES.length];
    const caseStatus = COMPLIANCE_QUEUE_STATUSES[i % COMPLIANCE_QUEUE_STATUSES.length];
    const caseType = CASE_TYPES[i % CASE_TYPES.length];
    const items = COMPLIANCE_ITEMS_BY_TYPE[complianceType];
    const item = items[i % items.length];
    const businessTradeCategory = TRADE_CATEGORIES[i % TRADE_CATEGORIES.length];
    const sourceTradeCategory = item.tradeCategory || businessTradeCategory;
    const businessName = businessNames[i % businessNames.length];
    const businessId = `BUS-${String(200 + i).padStart(6, '0')}`;

    let support_team_member = TEAM_MEMBER_NAMES[i % TEAM_MEMBER_NAMES.length];
    if (caseStatus === 'New' && i % 3 === 0) {
      support_team_member = null;
    }

    const submittedDate = new Date(now);
    submittedDate.setDate(submittedDate.getDate() - (i % 90));
    submittedDate.setHours(9 + (i % 8), (i * 17) % 60, 0, 0);

    const lastUpdated = new Date(submittedDate);
    lastUpdated.setDate(lastUpdated.getDate() + (i % 10));
    lastUpdated.setHours(10 + (i % 7), (i * 13) % 60, 0, 0);

    extra.push({
      compliance_id: `CMP-${String(n).padStart(6, '0')}`,
      business_name: businessName,
      business_id: businessId,
      business_avatar: `https://i.pravatar.cc/150?img=${(i % 70) + 1}`,
      compliance_type: complianceType,
      compliance_item_id: item.id,
      compliance_item: item.name,
      trade_category: sourceTradeCategory,
      business_trade_category: businessTradeCategory,
      case_type: caseType,
      support_team_member,
      case_status: caseStatus,
      submitted_at: submittedDate.toISOString(),
      last_updated: lastUpdated.toISOString(),
    });
  }

  const withAvatars = samples.map((row, i) => ({
    ...row,
    business_avatar: `https://i.pravatar.cc/150?img=${(i % 70) + 1}`,
  }));

  return [...withAvatars, ...extra].map((row) => ({
    ...row,
    // Detail-page compatibility aliases
    assigned_to: row.support_team_member,
    category: row.compliance_type,
    document_name: row.compliance_item,
    verification_status: row.case_status,
  }));
};

const formatQueueDateTime = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const dateStr = date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  const h12 = hours % 12 || 12;
  const mm = String(minutes).padStart(2, '0');
  return `${dateStr}, ${h12}:${mm} ${ampm}`;
};

const TABLE_GRID =
  'minmax(0, 1.05fr) minmax(0, 1.2fr) minmax(0, 0.65fr) minmax(0, 1.15fr) minmax(0, 0.85fr) minmax(0, 0.95fr) minmax(0, 0.95fr) minmax(0, 0.95fr) minmax(62px, auto)';

const VerificationCompliance = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [documents] = useState(() => buildMockDocuments());
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [typeOpen, setTypeOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);

  const typeRef = useRef(null);
  const categoryRef = useRef(null);
  const statusRef = useRef(null);
  const [complianceQueueRowActionsOpen, setComplianceQueueRowActionsOpen] = useState({});
  const complianceQueueRowActionsRefs = useRef({});
  const tableScrollRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);

  useClickOutside(typeRef, () => setTypeOpen(false));
  useClickOutside(categoryRef, () => setCategoryOpen(false));
  useClickOutside(statusRef, () => setStatusOpen(false));

  const toggleComplianceQueueRowActions = (rowKey) => {
    setComplianceQueueRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(complianceQueueRowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setComplianceQueueRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const sessionDocuments = useMemo(
    () => documents.map((doc) => applyComplianceQueueSession(doc)),
    [documents, location.pathname]
  );

  const filteredDocuments = useMemo(() => {
    return sessionDocuments.filter((doc) => {
      if (filters.type !== 'All' && doc.compliance_type !== filters.type) return false;
      if (filters.category !== 'All' && doc.trade_category !== filters.category) return false;
      if (filters.status !== 'All' && doc.case_status !== filters.status) return false;
      if (debouncedSearch) {
        const q = debouncedSearch.toLowerCase();
        const haystack = [
          doc.compliance_id,
          doc.business_id,
          doc.business_name,
          doc.compliance_item_id,
          doc.compliance_item,
          doc.compliance_type,
          doc.trade_category,
        ]
          .filter((v) => v != null && v !== '')
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [sessionDocuments, filters, debouncedSearch]);

  const sortedDocuments = useMemo(() => {
    return [...filteredDocuments].sort(
      (a, b) => new Date(b.submitted_at) - new Date(a.submitted_at)
    );
  }, [filteredDocuments]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, debouncedSearch]);

  const totalCount = sortedDocuments.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage((p) =>
      Math.min(p, Math.max(1, Math.ceil(sortedDocuments.length / CONFIG_LIST_PAGE_SIZE)))
    );
  }, [sortedDocuments.length]);

  const paginatedDocuments = useMemo(() => {
    const start = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
    return sortedDocuments.slice(start, start + CONFIG_LIST_PAGE_SIZE);
  }, [sortedDocuments, effectivePage]);

  const rangeStart =
    totalCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalCount);
  const showPagination = totalCount > CONFIG_LIST_PAGE_SIZE;

  const goToDetail = (doc) => {
    navigate(`/admin/verification/${encodeURIComponent(doc.compliance_id)}`, {
      state: { document: doc, from: location.pathname },
    });
  };

  const closeDropdowns = () => {
    setTypeOpen(false);
    setCategoryOpen(false);
    setStatusOpen(false);
  };

  const typeFilterOptions = useMemo(
    () => [
      { value: 'All', label: 'All' },
      ...COMPLIANCE_TYPES.map((type) => ({ value: type, label: type })),
    ],
    []
  );

  const categoryFilterOptions = useMemo(
    () => [
      { value: 'All', label: 'All' },
      ...CATEGORY_FILTER_OPTIONS.map((category) => ({ value: category, label: category })),
    ],
    []
  );

  const statusFilterOptions = useMemo(
    () => [
      { value: 'All', label: 'All' },
      ...COMPLIANCE_QUEUE_STATUSES.map((status) => ({ value: status, label: status })),
    ],
    []
  );
  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Compliance Queue</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full max-w-[26.25rem]"
              type="text"
              placeholder="Search Compliance cases"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search Compliance cases"
            />
            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Type"
                value={filters.type}
                options={typeFilterOptions}
                onChange={(value) => setFilters((prev) => ({ ...prev, type: value }))}
                menuRef={typeRef}
                open={typeOpen}
                setOpen={setTypeOpen}
                size="lg"
              />
              <AdminListFilterDropdown
                label="Category"
                value={filters.category}
                options={categoryFilterOptions}
                onChange={(value) => setFilters((prev) => ({ ...prev, category: value }))}
                menuRef={categoryRef}
                open={categoryOpen}
                setOpen={setCategoryOpen}
                size="lg"
              />
              <AdminListFilterDropdown
                label="Case Status"
                value={filters.status}
                options={statusFilterOptions}
                onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
                menuRef={statusRef}
                open={statusOpen}
                setOpen={setStatusOpen}
                size="lg"
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div className="min-w-0">
              <div
                className="grid py-3 text-offblack border-b border-lightgray px-4 gap-2"
                style={{ gridTemplateColumns: TABLE_GRID }}
              >
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Case ID</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Business</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Type</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Name</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Trade Category</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Case Status</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Submitted</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Last Updated</AdminTableHeader>
                </div>
                <div className="flex items-center justify-center min-w-0">
                  <AdminTableHeader>Actions</AdminTableHeader>
                </div>
              </div>

              {sortedDocuments.length > 0 ? (
                paginatedDocuments.map((doc) => {
                  return (
                    <div
                      key={doc.compliance_id}
                      role="button"
                      tabIndex={0}
                      onClick={() => goToDetail(doc)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          goToDetail(doc);
                        }
                      }}
                      className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2 cursor-pointer hover:bg-pastalBlueWhite/50"
                      style={{ gridTemplateColumns: TABLE_GRID }}
                    >
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={doc.compliance_id}>
                          {doc.compliance_id}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0 gap-2">
                        <AdminRowAvatar
                          src={doc.business_avatar || ProfilePlaceholder}
                          alt={doc.business_name || ''}
                          onError={(e) => {
                            e.target.src = ProfilePlaceholder;
                          }}
                        />
                        <AdminTableBody emphasis className="truncate" title={doc.business_name}>
                          {doc.business_name || '—'}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={doc.compliance_type}>
                          {doc.compliance_type}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={doc.compliance_item}>
                          {doc.compliance_item}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={doc.trade_category}>
                          {doc.trade_category}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminStatusBadge status={doc.case_status} label={doc.case_status} />
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableMeta>{formatQueueDateTime(doc.submitted_at)}</AdminTableMeta>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableMeta>{formatQueueDateTime(doc.last_updated)}</AdminTableMeta>
                      </div>

                      <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                        <div
                          className="relative inline-flex"
                          ref={(el) => {
                            complianceQueueRowActionsRefs.current[doc.compliance_id] = el;
                          }}
                        >
                          <AdminRowActionsButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleComplianceQueueRowActions(doc.compliance_id);
                            }}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {complianceQueueRowActionsOpen[doc.compliance_id] && (
                            <div className={ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS}>
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeDropdowns();
                                  goToDetail(doc);
                                  setComplianceQueueRowActionsOpen({});
                                }}
                              >
                                Open Case
                              </AdminRowMenuItem>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="py-8 text-center">
                  <AdminSupportingText className="text-mediumgray">
                    No items match your search or filters.
                  </AdminSupportingText>
                </div>
              )}
            </div>
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={sortedDocuments.length > 0 && showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default VerificationCompliance;
